import { Box, styled } from "@mui/material";
import { ChevronLeft, ChevronRight, Minus, Plus } from "@procore/core-icons";
import { Dropdown } from "@procore/core-react";

const ControlBarContainer = styled(Box)({
    position: "absolute",
    bottom: 24,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2,
});

const ControlBarStyled = styled(Box)({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "8px",
    gap: "8px",
    background: "#FFFFFF",
    boxShadow: "0px 1px 4px rgba(40, 40, 40, 0.2), 0px 0px 3px rgba(0, 0, 0, 0.14), 0px 2px 3px rgba(0, 0, 0, 0.12)",
    borderRadius: 29,
});

const ControlButton = styled("button")({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 4,
    width: 24,
    height: 24,
    border: "none",
    background: "transparent",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": {
        background: "#F4F5F6",
    },
    "&:disabled": {
        opacity: 0.4,
        cursor: "not-allowed",
    },
});

const ControlDivider = styled(Box)({
    width: 1,
    height: 24,
    background: "#D6DADC",
});

const PageLabel = styled("span")({
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: 0.25,
    color: "#232729",
    whiteSpace: "nowrap",
});

const ZOOM_PRESETS = [
    { id: "100", label: "100%", value: 1 },
    { id: "150", label: "150%", value: 1.5 },
    { id: "200", label: "200%", value: 2 },
    { id: "250", label: "250%", value: 2.5 },
    { id: "300", label: "300%", value: 3 },
    { id: "350", label: "350%", value: 3.5 },
    { id: "400", label: "400%", value: 4 },
];

export interface ControlBarWebProps {
    currentPageIndex: number;
    totalPages: number;
    scale: number;
    onPrevPage: () => void;
    onNextPage: () => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomToValue: (value: number) => void;
}

export function ControlBarWeb({
    currentPageIndex,
    totalPages,
    scale,
    onPrevPage,
    onNextPage,
    onZoomIn,
    onZoomOut,
    onZoomToValue,
}: ControlBarWebProps) {
    return (
        <ControlBarContainer>
            <ControlBarStyled>
                <ControlButton onClick={onPrevPage} aria-label="Previous page">
                    <ChevronLeft style={{ width: 16, height: 16 }} />
                </ControlButton>
                <PageLabel>
                    {currentPageIndex + 1} of {totalPages}
                </PageLabel>
                <ControlButton onClick={onNextPage} aria-label="Next page">
                    <ChevronRight style={{ width: 16, height: 16 }} />
                </ControlButton>
                <ControlDivider />
                <Dropdown
                    label={`${Math.round(scale * 100)}%`}
                    variant="tertiary"
                    style={{ height: 24, minHeight: 24 }}
                    onSelect={({ item }) => {
                        const preset = ZOOM_PRESETS.find((p) => p.id === item);
                        if (preset) {
                            onZoomToValue(preset.value);
                        }
                    }}
                >
                    {ZOOM_PRESETS.map((preset) => (
                        <Dropdown.Item key={preset.id} item={preset.id}>
                            {preset.label}
                        </Dropdown.Item>
                    ))}
                </Dropdown>
                <ControlButton onClick={onZoomOut} disabled={scale <= 0.1} aria-label="Zoom out">
                    <Minus style={{ width: 16, height: 16 }} />
                </ControlButton>
                <ControlButton onClick={onZoomIn} disabled={scale >= 10} aria-label="Zoom in">
                    <Plus style={{ width: 16, height: 16 }} />
                </ControlButton>
            </ControlBarStyled>
        </ControlBarContainer>
    );
}
